import {DiffFileTree} from '@github-ui/diff-file-tree/file-tree'

import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {noop} from '@github-ui/noop'
import {clsx} from 'clsx'

import {type FileFilterState, type FileFilterMenuOptions, FileFilter} from '../diff-filtering/FileFilter'
import type {FileTreePayload, PullRequestFileTreeDiff} from '../../page-data/payloads/file-tree'
import styles from './PullRequestFileTree.module.css'

export type PullRequestFileTreeProps = Omit<FileTreePayload, 'commits' | 'baseRefOid'> & {
  fileFilterMenuOptions: FileFilterMenuOptions
  fileFilterState: FileFilterState
  filteredDiffs: PullRequestFileTreeDiff[] // filtered diff summaries
  viewerLogin?: string
  onFileSelected?: () => void
  setFileFilterState: (state: FileFilterState) => void
}

export const PR_FILE_TREE_ID = 'pr-file-tree'

/*
 * This component is intended to be visually and functionally consistent with the
 * @github-ui/commits FileTree component. Look for opportunities to upstream duplicated
 * code into the shared @github-ui/diff-file-tree package.
 */
export function PullRequestFileTree({
  onFileSelected = noop,
  fileFilterMenuOptions,
  fileFilterState,
  pathName: basePath,
  viewerLogin,
  setFileFilterState,
  filteredDiffs, // filtered diff summaries
}: PullRequestFileTreeProps) {
  return (
    <ErrorBoundary fallback={<span>File tree failed to load.</span>}>
      <div className={clsx('d-flex flex-column pt-3', styles['react-pr-files-file-tree'])} id={PR_FILE_TREE_ID}>
        <div className="pb-3 pr-3">
          <FileFilter
            basePath={basePath}
            fileFilterMenuOptions={fileFilterMenuOptions}
            fileFilterState={fileFilterState}
            setFileFilterState={setFileFilterState}
            viewerLogin={viewerLogin}
          />
        </div>

        <div style={{overflowY: 'auto'}}>
          <h2 className="sr-only">File tree</h2>
          <DiffFileTree diffs={filteredDiffs} renderPattern="traditional" onSelect={onFileSelected} className="pr-3" />
        </div>
      </div>
    </ErrorBoundary>
  )
}

try{ PullRequestFileTree.displayName ||= 'PullRequestFileTree' } catch {}